export interface DimensionConfirmation {
  skuIds: string[];
  familyStatus?: "confirmed";
  unitsStatus?: "confirmed";
  label: string;
}

interface ReviewFindingLike {
  id: string;
  severity: "hard_blocker" | "warning";
}

interface ReviewSkuLike {
  skuId: string;
  sold: boolean;
  classification: {
    status: string;
    familyStatus: "needs_review" | "confirmed" | null;
    unitsStatus: "needs_review" | "confirmed" | null;
    packSource: string | null;
    source: string;
    familyReviewBasis: string;
    unitsReviewBasis: string;
  };
  findings: ReviewFindingLike[];
}

interface ReviewProductLike {
  skus: ReviewSkuLike[];
  findings: ReviewFindingLike[];
}

interface AssumptionsReviewLike {
  summary: {
    hardBlockers: number;
    warnings: number;
  };
  products: ReviewProductLike[];
  ungroupedSkus: ReviewSkuLike[];
  findings: ReviewFindingLike[];
}

function relevantSkus(product: ReviewProductLike): ReviewSkuLike[] {
  return product.skus.filter((sku) => sku.sold
    && sku.classification.status !== "excluded"
    && sku.classification.status !== "retired");
}

/** One operator action records both independent checks when both are open. */
export function dimensionConfirmationForProduct(product: ReviewProductLike): DimensionConfirmation | null {
  const relevant = relevantSkus(product);
  const familyPending = relevant.filter((sku) => sku.classification.familyStatus !== "confirmed");
  const unitsPending = relevant.filter((sku) => sku.classification.unitsStatus !== "confirmed");
  const confirmFamily = familyPending.length > 0;
  const confirmUnits = unitsPending.length > 0;
  if (!confirmFamily && !confirmUnits) return null;

  const skuIds = relevant
    .filter((sku) => (confirmFamily && sku.classification.familyStatus !== "confirmed")
      || (confirmUnits && sku.classification.unitsStatus !== "confirmed"))
    .map((sku) => sku.skuId);
  const count = new Set(skuIds).size;
  return {
    skuIds: [...new Set(skuIds)],
    ...(confirmFamily ? { familyStatus: "confirmed" as const } : {}),
    ...(confirmUnits ? { unitsStatus: "confirmed" as const } : {}),
    label: confirmFamily && confirmUnits
      ? `I checked product family and units (${count})`
      : confirmFamily
        ? `I checked the product family (${count})`
        : `I checked units per sale (${count})`,
  };
}

function resolvedFindingIds(input: DimensionConfirmation): Set<string> {
  const ids = new Set<string>();
  for (const skuId of input.skuIds) {
    if (input.familyStatus === "confirmed") ids.add(`catalog.sold_sku_unconfirmed:${skuId}`);
    if (input.unitsStatus === "confirmed") {
      ids.add(`catalog.units_missing:${skuId}`);
      ids.add(`catalog.units_unconfirmed:${skuId}`);
    }
  }
  return ids;
}

/**
 * Reflect a successful review click immediately without rerunning the expensive
 * account scan. A manual refresh and the final approval endpoint still rebuild
 * the report from saved data before the account can be unlocked.
 */
export function applyOptimisticDimensionConfirmation<T extends AssumptionsReviewLike>(
  review: T,
  input: DimensionConfirmation,
): T {
  const skuIds = new Set(input.skuIds);
  const findingIds = resolvedFindingIds(input);
  const keepFinding = (finding: ReviewFindingLike): boolean => !findingIds.has(finding.id);
  const updateSku = <S extends ReviewSkuLike>(sku: S): S => {
    if (!skuIds.has(sku.skuId)) return sku;
    return {
      ...sku,
      classification: {
        ...sku.classification,
        ...(input.familyStatus === "confirmed" ? {
          familyStatus: "confirmed" as const,
          familyReviewBasis: "human",
          source: "Manual grouping",
        } : {}),
        ...(input.unitsStatus === "confirmed" ? {
          unitsStatus: "confirmed" as const,
          unitsReviewBasis: "human",
          packSource: "manual",
        } : {}),
      },
      findings: sku.findings.filter(keepFinding),
    };
  };
  const findings = review.findings.filter(keepFinding);
  return {
    ...review,
    summary: {
      ...review.summary,
      hardBlockers: findings.filter((finding) => finding.severity === "hard_blocker").length,
      warnings: findings.filter((finding) => finding.severity === "warning").length,
    },
    products: review.products.map((product) => ({
      ...product,
      skus: product.skus.map(updateSku),
      findings: product.findings.filter(keepFinding),
    })),
    ungroupedSkus: review.ungroupedSkus.map(updateSku),
    findings,
  } as T;
}
